import React, { type ErrorInfo, type ReactNode } from 'react';

import { isChunkLoadError, reloadOnceForChunkError } from '../../utils/chunkErrorRecovery';

import FallbackUI from './FallbackUI';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(info);
    console.error(error);

    if (this.state.isChunkError && !reloadOnceForChunkError()) {
      // Already tried an auto-reload this session and it failed again -
      // this is a real error, fall through to the error page.
      this.setState({ isChunkError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return null;
      }
      return <FallbackUI />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
